import {
	Box,
	Button,
	CircularProgress,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import * as NotificationActions from 'actions/NotificationActions';
import { api } from 'common/frontend/api';
import { Location } from 'common/types';
import Container from 'components/Container';
import ContainerBox from 'components/ContainerBox';
import SetPageNavAction from 'components/Portals/SetPageNavAction';
import SetPageTitle from 'components/Portals/SetPageTitle';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push, Routes } from 'routing';
import * as ShopSelectors from 'selectors/ShopSelectors';
import BackdropDialog from 'common/components/BackdropDialog';
import CheckboxItem from 'common/components/CheckboxItem';
import { Trans, useTranslation } from 'services/localization';
import { logEvent } from 'common/services/analytics';
import StoreTableRow from './components/StoreTableRow';
import { getShopLocations } from 'common/utils/shopUtils';
import { getSkuItemTotalQuantity } from 'common/modules/inventory';
import FeatureButton from 'components/features/FeatureButton';
import { Redirect } from 'react-router-dom';
import { Callable } from 'common/frontend/callable';

import MultipleStoresPromoDialog from 'components/features/promos/MultipleStoresPromoDialog';
import Banner from 'components/Banner';
import classNames from 'classnames';
import { moveItem } from 'common/utils/arrays';

interface LocationDeleteConfirmProps {
	location: Location;
	stockItemsCount: number;
	confirmed: boolean;
	loading?: boolean;
}

const StoresList = () => {
	const classes = useStyles();
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const shopPublicInfo = useSelector(ShopSelectors.activeShopPublicInfo);
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const shopFeatures = useSelector(ShopSelectors.shopFeatures);
	const mainLocationId = useSelector(ShopSelectors.activeShopMainLocationId);
	const shopBilling = useSelector(ShopSelectors.shopBilling);
	const locations = useMemo(() => getShopLocations(shopPublicInfo), [shopPublicInfo]);

	const [isReordering, setIsReordering] = useState<boolean>(false);
	const [saveOrderLoading, setSaveOrderLoading] = useState<boolean>(false);
	const [orderedStores, setOrderedStores] = useState<Location[]>(locations);
	const [upgradePromptOpen, setUpgradePromptOpen] = useState<boolean>(false);
	const [locationToDelete, setLocationToDelete] = useState<string | null>(null);
	const [
		locationDeleteConfirmProps,
		setLocationDeleteConfirmProps,
	] = useState<LocationDeleteConfirmProps | null>(null);

	const deletedStockItemsCount = locationDeleteConfirmProps?.stockItemsCount ?? 0;

	useEffect(() => {
		if (!isReordering) {
			setOrderedStores(locations);
		}
	}, [isReordering, locations]);

	const handleLocationDelete = async (location: Location) => {
		logEvent('delete_store_press');
		setLocationToDelete(location.id);
		try {
			const skuItems = await api().skuItems.get.where('shopId', '==', shopId).get();

			const locationSkuItems = skuItems.filter(
				(skuItem) => !!skuItem.statusesByLocation[location.id],
			);

			const itemsCount = getSkuItemTotalQuantity(locationSkuItems, location.id);

			setLocationDeleteConfirmProps({
				location,
				stockItemsCount: itemsCount,
				confirmed: itemsCount === 0,
			});
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);

			setLocationToDelete(null);
		}
	};

	const deleteLocation = async (location: Location) => {
		try {
			await Callable.plans.subscriptions.removeStore({
				merchantId: shopId,
				storeId: location.id,
			});
			dispatch(
				NotificationActions.showNotification({
					message: t('account.stores.storeDeleted', 'Store deleted'),
					variant: 'info',
				}),
			);
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		} finally {
			setLocationToDelete(null);
		}
	};

	const handleMoveStore = (storeId: string, direction: 'up' | 'down') => {
		const currentIndex = orderedStores.findIndex((item) => item.id === storeId);
		if (currentIndex === -1) return;
		const targetItem =
			direction === 'up' ? orderedStores[currentIndex - 1] : orderedStores[currentIndex + 1];
		if (!targetItem) return;
		const targetIndex = orderedStores.findIndex((item) => item.id === targetItem.id);
		if (targetIndex === -1) return;

		const placement = direction === 'up' ? 'before' : 'after';
		const result = moveItem(orderedStores, currentIndex, targetIndex, placement);
		setIsReordering(true);
		setOrderedStores(result);
	};

	const handleSaveReorder = async () => {
		const orderIndexByStoreId = orderedStores.reduce((result, store, index) => {
			result[store.id] = index;
			return result;
		}, {});

		setSaveOrderLoading(true);

		try {
			await api()
				.shops.doc(shopId)
				.public.update({
					'visitingAddress.orderIndex': orderIndexByStoreId[mainLocationId] ?? 0,
					shopLocations: locations
						.filter((location) => location.id !== mainLocationId)
						.map((location) => {
							return {
								...location,
								orderIndex: orderIndexByStoreId[location.id] ?? 0,
							};
						}),
				});
			setIsReordering(false);
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}

		setSaveOrderLoading(false);
	};

	const handleCancelReorder = () => {
		setOrderedStores(locations);
		setIsReordering(false);
	};

	if (!shopFeatures) {
		return <Redirect to={Routes.HOME_VIEW} />;
	}

	return (
		<>
			<SetPageTitle
				priority={2}
				title={t('account.stores.pageTitle', 'Stores')}
				rightContent={
					isReordering ? (
						<Stack direction="row">
							<Button
								color="secondary"
								variant="text"
								onClick={handleCancelReorder}
								disabled={saveOrderLoading}
							>
								{t('common:actions.cancel', 'Cancel')}
							</Button>
							<Button
								color="primary"
								variant="contained"
								onClick={handleSaveReorder}
								disabled={saveOrderLoading}
							>
								{saveOrderLoading ? (
									<CircularProgress size={20} color="inherit" />
								) : (
									t('common:actions.save', 'Save')
								)}
							</Button>
						</Stack>
					) : (
						<FeatureButton
							color="primary"
							variant="contained"
							requiredFeature={['STORES', 'MULTIPLE_STORES']}
							onClick={() => {
								logEvent('create_store_press');
								push(Routes.ACCOUNT_STORES_NEW);
							}}
							onFeaturesMissingClick={() => {
								setUpgradePromptOpen(true);
							}}
						>
							{t('account.stores.newStore', 'New store')}
						</FeatureButton>
					)
				}
			/>
			<SetPageNavAction
				priority={2}
				backAction={{ to: Routes.ACCOUNT, title: t('account.pageTitle', 'Account') }}
				quickLinks={[]}
			/>
			<Container>
				{shopFeatures.plan.plan === 'CORE_COMMISSION' && (
					<Box mb={5}>
						<Banner
							variant="info"
							title={'Rentle pricing will be updated on June 5th'}
							description={
								'Rentle will have a new subscription-based pricing model starting June 5th, 2022. You will be automatically moved to one of our new plans, with a special discount, and your monthly subscription price will depend on the amount of stores you have. Check out our blog post on the update to learn more!'
							}
							button={{
								text: 'Learn more',
								onClick: () =>
									window.open('https://www.rentle.io/blog/news/new-pricing?hsLang=en', '_blank'),
							}}
						/>
					</Box>
				)}
				<ContainerBox padding="none" className={classes.tableWrapper}>
					<Table>
						<TableHead className={classes.tableHead}>
							<TableRow>
								<TableCell align="left" className={classes.tableCell}>
									{t('account.stores.store', 'Store')}
								</TableCell>
								<TableCell align="left" className={classes.tableCell}>
									{t('account.stores.plan', 'Plan')}
								</TableCell>
								<TableCell align="left" className={classes.tableCell}>
									{t('account.stores.price', 'Price')}
								</TableCell>
								<TableCell className={classNames(classes.tableCell, classes.noStretch)}></TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{orderedStores.map((location, index) => (
								<StoreTableRow
									key={location.id}
									location={location}
									mainLocationId={mainLocationId}
									shopFeatures={shopFeatures}
									locationToDelete={locationToDelete}
									onDelete={handleLocationDelete}
									deleteDisabled={isReordering}
									shopPlan={shopFeatures.plan}
									shopBilling={shopBilling}
									onMoveUp={() => handleMoveStore(location.id, 'up')}
									onMoveDown={() => handleMoveStore(location.id, 'down')}
								/>
							))}
						</TableBody>
					</Table>
				</ContainerBox>
			</Container>

			<MultipleStoresPromoDialog
				open={upgradePromptOpen}
				onClose={() => setUpgradePromptOpen(false)}
			/>

			<BackdropDialog
				isOpen={!!locationDeleteConfirmProps}
				onClose={() => {
					setLocationToDelete(null);
					setLocationDeleteConfirmProps(null);
				}}
				label={
					deletedStockItemsCount > 0
						? t('account.stores.deleteStorePrompt', 'Delete store and inventory?')
						: t('account.stores.deleteStore', 'Delete store')
				}
				body={
					<Box>
						<Typography variant="body1">
							{deletedStockItemsCount > 0 ? (
								<Trans
									i18nKey="account.stores.deleteStoreDescription"
									defaults="<location></location> has <stockCount>{{count}} inventory items</stockCount> which will be deleted if you delete the store."
									values={{ count: deletedStockItemsCount }}
									components={{
										location: locationDeleteConfirmProps?.location.name,
										stockCount: <strong>{'inventory items'}</strong>,
									}}
								/>
							) : (
								t(
									'account.stores.confirmDelete',
									'Are you sure you want to delete this store? This action cannot be undone.',
								)
							)}
						</Typography>
						<Box mt={2} hidden={deletedStockItemsCount === 0 ? true : false}>
							<CheckboxItem
								checked={locationDeleteConfirmProps?.confirmed ?? false}
								onCheckedChange={(checked) =>
									setLocationDeleteConfirmProps((prev) =>
										!!prev ? { ...prev, confirmed: checked } : null,
									)
								}
								label={t(
									'account.stores.deleteStoreConfirm',
									'I understand that also all inventory items related to this store will be deleted.',
								)}
							/>
						</Box>
					</Box>
				}
				variant="danger"
				confirm={{
					label: t('common:actions.deleteStore', 'Delete store'),
					loading: locationDeleteConfirmProps?.loading ?? false,
					disabled: !locationDeleteConfirmProps?.confirmed,
					callback: () => {
						logEvent('delete_store_confirm');
						deleteLocation(locationDeleteConfirmProps!.location);
					},
				}}
				cancel={{
					label: t('common:actions.cancel'),
					callback: () => logEvent('delete_store_cancel'),
				}}
				width="sm"
			/>
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		tableWrapper: {
			boxShadow: 'none',
			maxHeight: '70vh',
			overflow: 'auto',
		},
		tableCell: {
			borderBottom: `1px solid ${theme.palette.border.table}`,
		},
		tableHead: {
			backgroundColor: theme.palette.background.secondary,
			whiteSpace: 'nowrap',
			'& > *': {
				verticalAlign: 'baseline',
			},
		},
		noStretch: {
			width: '2%',
		},
	}),
);

export default StoresList;
